/**
 * RecommendationsTab — the Briefing's Recommendations sub-tab (SYR-68, Phase 2).
 *
 * Renders the Briefing AI's `recommendations` cards (fix / win), numbers the
 * fixes, resolves each objective's `moves` element to its current BE score, and
 * lets the marketer commit cards to The Action Plan. Commit state is persisted
 * server-side per (org, brand) — committing snapshots the card so The Action
 * Plan survives AI regeneration (Phase 4).
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { CheckSquareOutlined, ReloadOutlined } from '@ant-design/icons';
import { useAuth } from '../../../hooks/useAuth';
import { useElementAccess } from '../../../hooks/useTierAccess';
import { UserRoleEnum } from '../../../enums/UserRoleEnum';
import {
  api,
  useGetMarketerActionPlanQuery,
  useAddMarketerActionPlanPlayMutation,
  useRemoveMarketerActionPlanPlayMutation,
  useDismissMarketerRecommendationMutation,
  useRegenerateMarketerRecommendationsMutation,
} from '../../../services/nestjs-base-api';
import type { MarketerViewData, ScoreNode } from '../types';
import type { BriefingNarrative } from '../useBriefingData';
import { useMarketerScope } from '../scope/MarketerScopeContext';
import { useMarketerRoot } from '../../../hooks/useDashboardConfig';
import { Card } from '../atoms';
import { LockedOverlay } from '../atoms/LockedOverlay';
import { THEME } from '../config/theme';
import { playStableId } from '../playStableId';
import { RecommendationCard, type Recommendation } from './RecommendationCard';

export interface RecommendationsTabProps {
  data: MarketerViewData;
  narrative: BriefingNarrative | null;
  /** Play ids deleted from the current set (server-persisted). */
  dismissedIds: string[];
  /** Refetch the briefing narrative after a regenerate. */
  refetchNarrative: () => void;
}

// Placeholder cards shown blurred behind the upgrade CTA for locked tiers — the
// real recommendations are never generated for them (saves AI tokens), so these
// exist purely to suggest the shape of what an upgrade unlocks.
const TEASER_RECOS: Recommendation[] = [
  {
    type: 'fix',
    title: 'Rebuild premium pricing power',
    rationale: 'Willingness to pay trails the category and is dragging equity.',
    objectives: [
      { task: 'Reframe value messaging', detail: 'Lead with the outcomes customers told you they value.', impact: 'High', effort: 'Med', moves: 'Willingness to Pay a Premium' },
    ],
  },
  {
    type: 'fix',
    title: 'Close the emotional gap',
    rationale: 'Brand attachment sits well below where loyalty needs it.',
    objectives: [
      { task: 'Launch a customer recognition program', detail: 'Reward repeat customers to deepen attachment.', impact: 'High', effort: 'Med', moves: 'Brand Attachment' },
    ],
  },
  {
    type: 'win',
    title: 'Lean into perceived quality',
    rationale: 'Quality is a relative strength worth amplifying.',
    objectives: [
      { task: 'Spotlight quality proof points', detail: 'Surface reviews and guarantees at point of sale.', impact: 'Med', effort: 'Low', moves: 'Perceived Quality' },
    ],
  },
  {
    type: 'win',
    title: 'Activate your best touchpoints',
    rationale: 'Touchpoints score well. Make them work harder.',
    objectives: [
      { task: 'Optimise the post-purchase journey', detail: 'Turn a strong experience into repeat visits.', impact: 'Med', effort: 'Low', moves: 'Touchpoints' },
    ],
  },
];

export const RecommendationsTab: React.FunctionComponent<RecommendationsTabProps> = ({
  data,
  narrative,
  dismissedIds,
  refetchNarrative,
}) => {
  const { brandHash } = useParams<{ brandHash: string }>();
  const navigate = useNavigate();
  const marketerRoot = useMarketerRoot();
  const { user } = useAuth();
  const jwtToken = user?.jwtToken ?? '';
  const organisationId = user?.organisation?.id ?? 0;
  const { activeBrand, payload } = useMarketerScope();
  const brandId = activeBrand?.id ?? 0;
  const { hasUnrestrictedAccess, isLoading: accessLoading } = useElementAccess();
  const locked = !accessLoading && !hasUnrestrictedAccess;
  // Viewers get the read-only experience: no delete, no regenerate.
  const canEdit = !user?.roles?.includes(UserRoleEnum.OrganisationViewer);
  const cards: Recommendation[] = useMemo(
    () => narrative?.recommendations?.recommendations ?? [],
    [narrative],
  );

  // Per-card delete (server-persisted on the current set; any regeneration
  // resets it). Local set gives optimistic hide before the server confirms.
  const [dismissCard] = useDismissMarketerRecommendationMutation();
  const [regenerateRecs, { isLoading: regenerating }] =
    useRegenerateMarketerRecommendationsMutation();
  const [localDismissed, setLocalDismissed] = useState<Set<string>>(new Set());
  const [confirmRegen, setConfirmRegen] = useState(false);
  // Minutes left on the server-side regenerate cooldown (429), shown inline.
  const [cooldownMinutes, setCooldownMinutes] = useState<number | null>(null);

  const dismissed = useMemo(() => {
    const s = new Set(dismissedIds);
    localDismissed.forEach((id) => s.add(id));
    return s;
  }, [dismissedIds, localDismissed]);

  const visibleCards = useMemo(
    () => cards.filter((c) => !dismissed.has(playStableId(c))),
    [cards, dismissed],
  );

  // Resolve a `moves` element name (canonical or display) → its ScoreNode.
  const byName = useMemo(() => {
    const m = new Map<string, ScoreNode>();
    data.signals.forEach((s) => {
      m.set(s.canonical, s);
      m.set(s.display, s);
    });
    return m;
  }, [data.signals]);
  const resolve = useCallback((name: string) => byName.get(name), [byName]);

  // Committed plays (server-persisted per org+brand). No tagTypes → refetch.
  const canQuery = !!jwtToken && !!organisationId && !!brandId;
  const { data: committedPlays, refetch } = useGetMarketerActionPlanQuery(
    { jwtToken, organisationId, brandId },
    { skip: !canQuery },
  );
  const [addPlay] = useAddMarketerActionPlanPlayMutation();
  const [removePlay] = useRemoveMarketerActionPlanPlayMutation();

  const added = useMemo(
    () => new Set((committedPlays ?? []).map((p) => p.playId)),
    [committedPlays],
  );

  const toggle = useCallback(
    async (card: Recommendation) => {
      if (!canQuery) return;
      const playId = playStableId(card);
      try {
        if (added.has(playId)) {
          await removePlay({ jwtToken, organisationId, brandId, playId }).unwrap();
        } else {
          await addPlay({
            jwtToken,
            organisationId,
            brandId,
            playId,
            snapshot: card,
          }).unwrap();
        }
        refetch();
      } catch {
        /* surfaced by RTK; leave UI state to the refetch */
      }
    },
    [added, addPlay, removePlay, refetch, canQuery, jwtToken, organisationId, brandId],
  );

  const dispatch = useDispatch();

  const handleDismiss = useCallback(
    async (card: Recommendation) => {
      if (!canQuery || !canEdit) return;
      const id = playStableId(card);
      setLocalDismissed((prev) => new Set(prev).add(id));
      try {
        await dismissCard({ jwtToken, organisationId, brandId, cardId: id }).unwrap();
        // Patch the cached briefing query too: it's held for 10 min
        // (keepUnusedDataFor), so without this a navigate-away-and-back would
        // serve the pre-deletion dismissed list and resurrect the card.
        if (payload) {
          dispatch(
            api.util.updateQueryData(
              'getMarketerBriefing',
              { jwtToken, organisationId, brandId, payload },
              (draft: { dismissedRecommendationIds?: string[] }) => {
                const ids = new Set(draft.dismissedRecommendationIds ?? []);
                ids.add(id);
                draft.dismissedRecommendationIds = Array.from(ids);
              },
            ) as any,
          );
        }
      } catch {
        // Server didn't record it — restore the card.
        setLocalDismissed((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [canQuery, canEdit, dismissCard, jwtToken, organisationId, brandId, payload, dispatch],
  );

  // Two-step regenerate (first click arms, second confirms) — it replaces the
  // whole set and clears deletions, so a stray click shouldn't trigger it.
  const handleRegenerate = useCallback(async () => {
    if (!canQuery || !canEdit || !payload || regenerating) return;
    if (!confirmRegen) {
      setConfirmRegen(true);
      window.setTimeout(() => setConfirmRegen(false), 4000);
      return;
    }
    setConfirmRegen(false);
    try {
      await regenerateRecs({ jwtToken, organisationId, brandId, payload }).unwrap();
      setLocalDismissed(new Set());
      refetchNarrative();
    } catch (e: any) {
      // Server cooldown (each regeneration is a full AI run) — tell the user
      // when they can try again; other errors keep the current set silently.
      const retryAfterSeconds = e?.data?.retryAfterSeconds;
      if (e?.status === 429 && typeof retryAfterSeconds === 'number') {
        setCooldownMinutes(Math.max(1, Math.ceil(retryAfterSeconds / 60)));
        window.setTimeout(() => setCooldownMinutes(null), 8000);
      }
    }
  }, [
    canQuery,
    canEdit,
    payload,
    regenerating,
    confirmRegen,
    regenerateRecs,
    jwtToken,
    organisationId,
    brandId,
    refetchNarrative,
  ]);

  // Number the fixes in order (wins are unnumbered).
  const numbered = useMemo(() => {
    let f = 0;
    return visibleCards.map((c) => ({ card: c, fixNo: c.type === 'fix' ? ++f : null }));
  }, [visibleCards]);

  // Locked tier: the recommendations AI was never run (token saver). Show the
  // header in the clear, blur teaser cards, and prompt to upgrade.
  if (locked) {
    return (
      <div style={{ paddingTop: 8 }}>
        <div style={{ marginBottom: 18 }}>
          <h2
            style={{
              fontSize: 22,
              fontWeight: 600,
              color: THEME.textBase,
              letterSpacing: '-0.02em',
              margin: 0,
            }}
          >
            <FormattedMessage
              id="marketerView.recos.title"
              defaultMessage="Your recommended moves"
            />
          </h2>
          <p style={{ fontSize: 14, color: THEME.textSecondary, margin: '6px 0 0', maxWidth: 720 }}>
            <FormattedMessage
              id="marketerView.recos.lockedSub"
              defaultMessage="Upgrade to turn your Brand Equity data into prioritised fixes and quick wins you can commit to The Action Plan."
            />
          </p>
        </div>
        <LockedOverlay
          locked
          message={
            <FormattedMessage
              id="marketerView.recos.lockedCta"
              defaultMessage="Upgrade to unlock your recommendations"
            />
          }
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: 18,
              alignItems: 'start',
            }}
          >
            {TEASER_RECOS.map((card, i) => (
              <RecommendationCard
                key={`teaser-${i}`}
                card={card}
                fixNo={card.type === 'fix' ? i + 1 : null}
                added={false}
                onToggle={() => {}}
                resolve={() => undefined}
              />
            ))}
          </div>
        </LockedOverlay>
      </div>
    );
  }

  const cooldownNotice =
    cooldownMinutes != null ? (
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: '#B45309',
          background: '#FFFBEB',
          border: '1px solid #FCD34D',
          borderRadius: 10,
          padding: '8px 12px',
          marginBottom: 14,
        }}
      >
        <FormattedMessage
          id="marketerView.recos.regenCooldown"
          defaultMessage="These recommendations were generated recently. Regenerating re-runs the AI, so it's limited: try again in about {minutes, plural, one {# minute} other {# minutes}}."
          values={{ minutes: cooldownMinutes }}
        />
      </div>
    ) : null;

  const regenButton = canEdit ? (
    <button
      type="button"
      onClick={handleRegenerate}
      disabled={regenerating}
      style={{
        border: `1px solid ${confirmRegen ? '#FCD34D' : THEME.borderSubtle}`,
        background: confirmRegen ? '#FFFBEB' : '#fff',
        color: confirmRegen ? '#B45309' : THEME.textSecondary,
        fontSize: 13.5,
        fontWeight: 600,
        padding: '8px 14px',
        borderRadius: 10,
        cursor: regenerating ? 'default' : 'pointer',
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      <ReloadOutlined style={{ marginRight: 6 }} spin={regenerating} />
      {regenerating ? (
        <FormattedMessage
          id="marketerView.recos.regenerating"
          defaultMessage="Regenerating..."
        />
      ) : confirmRegen ? (
        <FormattedMessage
          id="marketerView.recos.regenerateConfirm"
          defaultMessage="Replace all cards?"
        />
      ) : (
        <FormattedMessage
          id="marketerView.recos.regenerate"
          defaultMessage="Regenerate"
        />
      )}
    </button>
  ) : null;

  if (cards.length === 0) {
    return (
      <div style={{ paddingTop: 8 }}>
        <Card>
          <div style={{ textAlign: 'center', padding: '56px 24px' }}>
            <CheckSquareOutlined style={{ fontSize: 40, color: '#CBD5E1' }} />
            <h2
              style={{
                fontSize: 22,
                fontWeight: 600,
                color: THEME.textBase,
                letterSpacing: '-0.02em',
                margin: '16px 0 8px',
              }}
            >
              <FormattedMessage
                id="marketerView.recos.preparingTitle"
                defaultMessage="Preparing your recommendations"
              />
            </h2>
            <p style={{ fontSize: 15, color: THEME.textSecondary, margin: '0 auto', maxWidth: 460 }}>
              <FormattedMessage
                id="marketerView.recos.preparingBody"
                defaultMessage="We're turning your Brand Equity data into prioritised fixes and quick wins, check back in a moment."
              />
            </p>
          </div>
        </Card>
      </div>
    );
  }

  if (visibleCards.length === 0) {
    return (
      <div style={{ paddingTop: 8 }}>
        <Card>
          <div style={{ textAlign: 'center', padding: '56px 24px' }}>
            <CheckSquareOutlined style={{ fontSize: 40, color: '#CBD5E1' }} />
            <h2
              style={{
                fontSize: 22,
                fontWeight: 600,
                color: THEME.textBase,
                letterSpacing: '-0.02em',
                margin: '16px 0 8px',
              }}
            >
              <FormattedMessage
                id="marketerView.recos.allRemovedTitle"
                defaultMessage="All recommendations removed"
              />
            </h2>
            <p style={{ fontSize: 15, color: THEME.textSecondary, margin: '0 auto', maxWidth: 460 }}>
              <FormattedMessage
                id="marketerView.recos.allRemovedBody"
                defaultMessage="You removed every card in this set. Regenerate to get a fresh set of recommendations."
              />
            </p>
            <div style={{ marginTop: 18, display: 'flex', justifyContent: 'center' }}>
              {regenButton}
            </div>
            {cooldownNotice && (
              <div
                style={{
                  marginTop: 14,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                {cooldownNotice}
              </div>
            )}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: 8 }}>
      <div
        style={{
          marginBottom: 18,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
        }}
      >
        <div>
          <h2
            style={{
              fontSize: 22,
              fontWeight: 600,
              color: THEME.textBase,
              letterSpacing: '-0.02em',
              margin: 0,
            }}
          >
            <FormattedMessage
              id="marketerView.recos.title"
              defaultMessage="Your recommended moves"
            />
          </h2>
          <p style={{ fontSize: 14, color: THEME.textSecondary, margin: '6px 0 0', maxWidth: 720 }}>
            <FormattedMessage
              id="marketerView.recos.sub"
              defaultMessage={
                'Prioritised fixes and quick wins from your Brand Equity data.{br}<b>These are recommendations from the AI "Head of Brand Marketing" expert, as guidance rather than prescriptive actions.</b>'
              }
              values={{
                br: <br />,
                b: (chunks) => <strong>{chunks}</strong>,
              }}
            />
          </p>
        </div>
        {regenButton}
      </div>
      {cooldownNotice}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          padding: '12px 18px',
          borderRadius: 12,
          marginBottom: 18,
          fontSize: 14,
          fontWeight: added.size > 0 ? 600 : 500,
          border: `1px solid ${added.size > 0 ? '#A7F3D0' : THEME.borderSubtle}`,
          background: added.size > 0 ? '#F0FDF4' : THEME.bgTinted,
          color: added.size > 0 ? '#047857' : THEME.textSecondary,
        }}
      >
        <span>
          {added.size > 0 ? (
            <FormattedMessage
              id="marketerView.recos.bannerAdded"
              defaultMessage="{n, plural, one {# play} other {# plays}} added to your Action plan"
              values={{ n: added.size }}
            />
          ) : (
            <FormattedMessage
              id="marketerView.recos.bannerNone"
              defaultMessage="No plays added yet. Tick a card to start building your Action plan."
            />
          )}
        </span>
        {added.size > 0 && (
          <button
            type="button"
            onClick={() => brandHash && navigate(`${marketerRoot}/${brandHash}/action-plan`)}
            style={{
              border: 'none',
              background: THEME.primary,
              color: '#fff',
              fontSize: 13.5,
              fontWeight: 700,
              padding: '9px 16px',
              borderRadius: 10,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <FormattedMessage
              id="marketerView.recos.goToPlan"
              defaultMessage="Go to Action plan →"
            />
          </button>
        )}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
          gap: 18,
          alignItems: 'start',
        }}
      >
        {numbered.map(({ card, fixNo }, i) => {
          const id = playStableId(card) || `card-${i}`;
          return (
            <RecommendationCard
              key={id}
              card={card}
              fixNo={fixNo}
              added={added.has(id)}
              onToggle={() => toggle(card)}
              onDismiss={canEdit ? () => handleDismiss(card) : undefined}
              resolve={resolve}
            />
          );
        })}
      </div>
    </div>
  );
};
